import {
  createContext,
  useContext,
  useState,
  ReactNode,
  useRef,
  useEffect,
} from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { PostStatus } from '@kontentino/kontentino-constants/Posts';
import { ApiClientError } from 'api/client';
import { useTranslation } from 'react-i18next';
import PostApi from 'api/post';
import { queryKey } from 'constants/queryKey';
import { useToast } from 'app/hooks/useToast';
import { ApprovalThreadType } from '../types';

export interface ApprovedPost {
  id: number;
  title: string;
  thumbnail: string | null;
  previousStatus: PostStatus;
  thread: ApprovalThreadType;
  pageId: number;
  pageType: number;
}

interface ApprovedPostsContextValue {
  approvedPosts: ApprovedPost[];
  addApprovedPost: (post: ApprovedPost) => void;
  revertApproval: (postId: number) => void;
  isRevertingApproval: (postId: number) => boolean;
}

export const ApprovedPostsContext = createContext<
  ApprovedPostsContextValue | undefined
>(undefined);

export const ApprovedPostsProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [approvedPosts, setApprovedPosts] = useState<ApprovedPost[]>([]);
  const [revertingPostIds, setRevertingPostIds] = useState<number[]>([]);
  const pendingMutationsRef = useRef(0);
  const isMountedRef = useRef(true);
  const queryClient = useQueryClient();
  const toast = useToast();
  const { t } = useTranslation();

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const revertApprovalMutation = useMutation(
    async (post: ApprovedPost) => {
      pendingMutationsRef.current++;
      await PostApi.editPostStatus({
        id: post.id,
        status: post.previousStatus,
      });
    },
    {
      onSettled: (_, error, post) => {
        pendingMutationsRef.current--;

        if (!isMountedRef.current) return;

        if (!error) {
          setApprovedPosts((prev) => prev.filter((p) => p.id !== post.id));
        }
        setRevertingPostIds((prev) => prev.filter((id) => id !== post.id));

        if (pendingMutationsRef.current === 0) {
          queryClient.invalidateQueries(queryKey.approvalsPosts());
          queryClient.invalidateQueries(queryKey.postsMeta());
        }
      },
      onSuccess: (_, post) => {
        if (!isMountedRef.current) return;

        toast(
          t(`approvalUndone.${post.thread}`, {
            defaultValue:
              post.thread === 'internal'
                ? 'Post status reverted to pending internal approval'
                : 'Post status reverted to pending client approval',
          }),
          'success',
        );
      },
      onError: (e: ApiClientError) => {
        if (!isMountedRef.current) return;

        toast(
          e.userMessage ??
            t('somethingWentWrongTryAgain', {
              defaultValue: 'Something went wrong. Please try again.',
            }),
          'error',
        );
      },
    },
  );

  const addApprovedPost = (post: ApprovedPost) => {
    setApprovedPosts((prev) => {
      if (prev.some((p) => p.id === post.id)) {
        return prev;
      }
      return [...prev, post];
    });
  };

  const isRevertingApproval = (postId: number) => {
    return revertingPostIds.includes(postId);
  };

  const revertApproval = (postId: number) => {
    if (revertingPostIds.includes(postId)) return;

    const post = approvedPosts.find((p) => p.id === postId);
    if (post) {
      setRevertingPostIds((prev) => [...prev, postId]);
      revertApprovalMutation.mutate(post);
    }
  };

  return (
    <ApprovedPostsContext.Provider
      value={{
        approvedPosts,
        addApprovedPost,
        revertApproval,
        isRevertingApproval,
      }}
    >
      {children}
    </ApprovedPostsContext.Provider>
  );
};

export const useApprovedPosts = () => {
  const context = useContext(ApprovedPostsContext);
  if (context === undefined) {
    throw new Error(
      'useApprovedPosts must be used within ApprovedPostsProvider',
    );
  }
  return context;
};
